import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import WhatsAppLogo from "@/components/site/WhatsAppLogo";
import { useI18n } from "@/i18n/I18nProvider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CollectionPage() {
  const { slug } = useParams();
  const { t } = useI18n();
  const [collection, setCollection] = useState(null);
  const [products, setProducts] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/collections`),
      axios.get(`${API}/products?collection=${slug}`),
    ]).then(([cRes, pRes]) => {
      const c = (cRes.data || []).find((x) => x.slug === slug);
      if (!c) { setNotFound(true); return; }
      setCollection(c);
      setProducts(pRes.data || []);
    }).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <Navigate to="/products" replace />;
  if (!collection) return <div className="pt-40 pb-20 text-center text-slate-400">Loading…</div>;

  return (
    <div className="pt-24 md:pt-28 pb-20 bg-white">
      {/* Hero */}
      <section className="relative bg-slate-50 border-b border-slate-100">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-12 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-6">
            <Link to="/products" className="inline-flex items-center gap-1.5 text-slate-500 hover:text-slate-900 text-xs font-grotesk font-semibold uppercase tracking-[0.2em] mb-6 transition-colors">
              <ArrowLeft size={12} /> All products
            </Link>
            <div className="label-eyebrow mb-3">Collection</div>
            <h1 className="font-display font-medium text-slate-900 text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">{collection.title}</h1>
            <p className="mt-6 text-slate-600 text-base md:text-lg leading-relaxed max-w-xl">{collection.description}</p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/contact" className="btn-steel inline-flex items-center gap-2 px-6 py-3.5 text-xs font-grotesk font-semibold uppercase tracking-[0.18em] rounded-full">
                Request a quote <ArrowUpRight size={14} />
              </Link>
              <a href={`https://wa.me/919731852956?text=${encodeURIComponent(`Hi Ramdev Steel, I'm interested in ${collection.title}.`)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#25D366] hover:bg-[#1EBE57] text-white text-xs font-grotesk font-semibold uppercase tracking-[0.18em] rounded-full transition-colors">
                <WhatsAppLogo size={14} /> WhatsApp
              </a>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-slate-100 shadow-lg">
              {collection.image ? (
                <img src={collection.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <div className="absolute inset-0 grid place-items-center bg-gradient-to-br from-[var(--steel-soft)] to-slate-100">
                  <span className="label-eyebrow text-slate-400">Photos coming soon</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="max-w-[1440px] mx-auto px-5 md:px-10 py-16 md:py-24">
        {products.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {products.map((p, i) => (
              <motion.article
                key={p.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="card-hover group rounded-xl overflow-hidden bg-white border border-slate-200"
              >
                <div className="relative aspect-[4/5] bg-slate-100 overflow-hidden">
                  {p.image ? (
                    <img src={p.image} alt="" loading="lazy" className="pc-img absolute inset-0 w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 grid place-items-center bg-gradient-to-br from-[var(--steel-soft)] to-slate-100">
                      <span className="label-eyebrow text-slate-400">Photo coming soon</span>
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-display text-slate-900 text-lg leading-tight tracking-tight mb-2">{p.title}</h3>
                  <p className="text-slate-600 text-[13.5px] leading-relaxed line-clamp-2 mb-4">{p.description}</p>
                  <Link to="/contact" className="inline-flex items-center gap-1.5 text-[var(--steel)] hover:text-[var(--steel-hover)] text-[11px] font-grotesk font-semibold uppercase tracking-[0.2em] transition-colors">
                    Enquire <ArrowUpRight size={12} />
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-slate-500 mb-6">No products listed yet in this collection.</p>
            <Link to="/contact" className="btn-steel inline-flex items-center gap-2 px-6 py-3.5 text-xs font-grotesk font-semibold uppercase tracking-[0.18em] rounded-full">
              WhatsApp for samples <WhatsAppLogo size={14} />
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}
